import { Navigate } from 'react-router-dom';

import { SuspendLayout } from 'components/main-layout';

import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import { checkPermissionOnRoute } from 'hooks/useCheckPermissions';
import { useQueryBrandingNavigation } from 'hooks/useGetBranding';
import { useUserInfo } from 'hooks';
import { useUserWorkflowRoles } from 'pages/home/header/hooks/useUserWorkflowRoles';

import { defineRulesFor, defineRulesForWorkflow } from 'common/casl/ability';

export const SECURE_CONFIG = {
  ALL_AUTH: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.ALL_AUTH],
  },
  USER: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.USER],
  },
  PRODUCT: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.PRODUCT],
  },
  INGREDIENT_COMPLIANCE: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.INGREDIENT_COMPLIANCE],
  },
  PRODUCT_APL: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.PRODUCT_APL],
  },
  MEMBER: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.MEMBER],
  },
  EDIT_MEMBER: {
    PASS: [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.MEMBER],
  },
  COMPANY_PROFILE: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.COMPANY_PROFILE],
  },
  EDIT_COMPANY_PROFILE: {
    PASS: [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.COMPANY_PROFILE],
  },
  ASSET: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.ASSET],
  },
  ASSET_CREATE: {
    PASS: [ABILITY_ACTION.CREATE, ABILITY_SUBJECT.ASSET],
  },
  ASSET_EDIT: {
    PASS: [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.ASSET],
  },
  FOLDER: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.FOLDER],
  },
  HELP_CATEGORY: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.HELP_CATEGORY],
  },
  HELP_POST: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.HELP_POST],
  },
  HELP_POST_MANAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.HELP_POST],
  },
  ARTICLE: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.ARTICLE],
  },
  ARTICLE_MANAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.ARTICLE],
  },
  EULA: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.EULA],
  },
  EULA_MANAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.EULA],
  },
  COMMUNICATION_TEMPLATE: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.COMMUNICATION_TEMPLATE],
  },
  COMMUNICATION_TEMPLATE_MANAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.COMMUNICATION_TEMPLATE],
  },
  REPORTING: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.REPORTING],
  },
  REPORTING_CREATE: {
    PASS: [ABILITY_ACTION.CREATE, ABILITY_SUBJECT.REPORTING],
  },
  SHARED_ENTITY: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.SHARED_ENTITY],
  },
  MANAGE_MEMBER: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.MANAGE_MEMBER],
  },
  SUPER_ADMIN: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.ALL],
  },
  EDIT_USER_PERMISSION: {
    PASS: [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.USER_PERMISSIONS],
  },
  WORKFLOW: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.WORKFLOW],
  },
  WORKFLOW_MAINTENANCE: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.WORKFLOW_MAINTENANCE],
  },
  WORKFLOW_ADMINISTRATION: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.WORKFLOW_ADMINISTRATION],
  },
  WORKFLOW_DEFINITION: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.WORKFLOW_DEFINITION],
  },
  CHAT_PERSONAL: {
    PASS: [ABILITY_ACTION.CHAT, ABILITY_SUBJECT.CHAT_PERSONAL],
  },
  CHAT_COMPANY: {
    PASS: [ABILITY_ACTION.CHAT, ABILITY_SUBJECT.CHAT_COMPANY],
  },
  MARKUP: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.MARKUP],
  },
  MARKUP_MANAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MARKUP],
  },
  CURATED_QUERY: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.CURATED_QUERY],
  },
  MY_QUERY: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.MY_QUERY],
  },
  SHARED_QUERY: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.SHARED_QUERY],
  },
  RECIPIENTS_FIELDS: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.RECIPIENTS_FIELDS],
  },
  MANAGE_PIM_MAPPING: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.PIM_MAPPING],
  },
  MANAGE_SSO_APPROVAL: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.ALL],
  },
  ACCESS_GDSN: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.GDSN],
  },
  ACCESS_SYNDICATION: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.SYNDICATION],
  },
  VIEW_NEW_ITEM_FORMS: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.NEW_ITEM_FORMS],
  },
  MANAGE_IMPERSONATE: {
    PASS: [
      [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.COMPANY_PROFILE],
      [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.MEMBER],
    ],
  },
  VIEW_QA_SPECIFICATION: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.QA_SPECIFICATION],
  },
  VIEW_EVALUATION_FORM: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.EVALUATION_FORM],
  },
  MANAGE_BILLABLE_TRACKING: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.BILLABLE_TRACKING],
  },
  DOWNLOAD_SPLASH_HISTORY: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.SPLASH_DOWNLOAD_HISTORY],
  },
  ACCESS_WORKFLOW: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.WORKFLOW],
  },
  TICKET_SYSTEM: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.TICKET_SYSTEM],
  },
  TICKET_GROUP: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.TICKET_SYSTEM_GROUP],
  },
  MANAGE_QA_SPEC_WORKFLOW: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.QA_SPEC_WORKFLOW],
  },
  ACCESS_GDSN_RECEIVED_PRODUCT: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.GDSN_RECEIVED_PRODUCT],
  },
  ACCESS_MY_SUBSCRIPTIONS: {
    PASS: [ABILITY_ACTION.ACCESS, ABILITY_SUBJECT.MY_SUBSCRIPTIONS],
  },
  MANAGE_MEMBER_ROLE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MEMBER_ROLE],
  },
  MANAGE_MFA_VERIFICATION: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MFA_VERIFICATION],
  },
  MANAGE_REPORT_SECTION: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.REPORT_SECTION],
  },
  MANAGE_FORM_MANAGEMENT: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.FORM_MANAGEMENT],
  },
  MANAGE_TRANSITION_PAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.TRANSITION_PAGE],
  },
  MANAGE_MINIMUM_REQUIREMENTS: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MINIMUM_REQUIREMENTS],
  },
  MANAGE_SSO_USER_APPROVAL: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.SSO_USER_APPROVAL],
  },
  MANAGE_SSO_MEMBER_APPROVAL: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.SSO_MEMBER_APPROVAL],
  },
  MANAGE_MEMBER_MANAGEMENT: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MEMBER_MANAGEMENT],
  },
  MANAGE_USER_MANAGEMENT: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.USER_MANAGEMENT],
  },
  MANAGE_MAIN_PAGE: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MAIN_PAGE],
  },
  MEMBER_BILLING_MANAGEMENT: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MEMBER_BILLING_MANAGEMENT],
  },
  MANAGE_MEMBER_CREDIT_CARD: {
    PASS: [ABILITY_ACTION.MAINTAIN, ABILITY_SUBJECT.MEMBER_CREDIT_CARD],
  },
  OVERLAY: {
    OR: [
      [ABILITY_ACTION.CREATE, ABILITY_SUBJECT.PRODUCT_OVERLAY],
      [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.PRODUCT_OVERLAY],
      [ABILITY_ACTION.DELETE, ABILITY_SUBJECT.PRODUCT_OVERLAY],
    ],
  },
  OVERLAY_CREATE: {
    PASS: [ABILITY_ACTION.CREATE, ABILITY_SUBJECT.PRODUCT_OVERLAY],
  },
  OVERLAY_EDIT: {
    PASS: [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.PRODUCT_OVERLAY],
  },
  MANAGE_RIVIR_TC: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.RIVIR_TC],
  },
  VIEW_RIVIR_TC: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.RIVIR_TC],
  },
  MANAGE_RENEWAL_NOTIFICATION: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.RENEWAL_NOTIFICATION],
  },
  VIEW_RENEWAL_NOTIFICATION: {
    PASS: [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.RENEWAL_NOTIFICATION],
  },
  MANAGE_ORDER: {
    PASS: [ABILITY_ACTION.MANAGE, ABILITY_SUBJECT.ORDER],
  },
};

export const createSuspenseRoute = ({
  secureConfig,
  Component,
  componentProps = {},
  operator = 'or',
}) => {
  const WrappedComponent = WithSecureRoute({ secureConfig, operator })(
    Component,
    componentProps
  );

  return (
    <SuspendLayout>
      <WrappedComponent />
    </SuspendLayout>
  );
};

const WithSecureRoute =
  ({ secureConfig, operator }) =>
  (Component, additionalProps) =>
  (props) => {
    return (
      <ImprovedCan
        secureConfig={secureConfig}
        operator={operator}
        Component={Component}
        additionalProps={additionalProps}
        componentProps={props}
      />
    );
  };

const ImprovedCan = (props) => {
  const { secureConfig, operator, Component, additionalProps, componentProps } =
    props;

  const { userInfo } = useUserInfo();

  const { workflowRoles } = useUserWorkflowRoles({ id: userInfo?.id });

  const { brandingNavigation: navigationData } = useQueryBrandingNavigation();

  const permissionRules = defineRulesFor(userInfo, navigationData);
  const workflowRules = defineRulesForWorkflow(
    userInfo,
    workflowRoles,
    navigationData
  );

  const rules = [...permissionRules, ...workflowRules];
  const allowed = checkPermissionOnRoute({
    rules,
    permissions: secureConfig,
    operator,
  });

  if (!userInfo) {
    return <Navigate to='/login' replace />;
  }

  if (allowed) {
    return <Component {...componentProps} {...additionalProps} />;
  } else {
    return <Navigate to='/dashboard' replace />;
  }
};

export default WithSecureRoute;
